// src/layouts/MemberLayout.tsx
import React, { useState, useEffect, useMemo } from "react";
import { Link, Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  LogOut,
  Menu,
  X,
  Settings,
  Home,
  BookOpen,
  ClipboardList,
  TrendingUp,
  Star,
  Users,
} from "lucide-react";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { useLogoutMutation, MemberTier } from "../app/api/authApi";
import { QuarterKey, useGetMyQuarterQuery } from "../app/api/programmeApi";
import { useGetTasksQuery } from "../app/api/tasksApi";
import { clearCredentials } from "../app/features/authSlice";
import OnboardingModal from "./OnboardingModal";
import WelcomeModal from "./WelcomeModal";

const TIER_STYLES: Record<MemberTier, string> = {
  wider_audience: "bg-green-50 text-green-700 border-green-200",
  inner_circle: "bg-blue-50 text-blue-700 border-blue-200",
  legacy_fellows: "bg-purple-50 text-purple-700 border-purple-200",
};

const TIER_LABELS: Record<MemberTier, string> = {
  wider_audience: "Wider Audience",
  inner_circle: "Inner Circle",
  legacy_fellows: "Legacy Fellows",
};

const isCompleted = (status?: string) =>
  status === "submitted" || status === "graded";

const MemberLayout: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    return window.innerWidth >= 768;
  });

  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const [logoutApi] = useLogoutMutation();

  const { user } = useAppSelector((s) => s.auth);

  const { data: myQuarterRes, isLoading: quarterLoading } =
    useGetMyQuarterQuery();
  const cycle = myQuarterRes?.data?.cycle;
  const myQData = myQuarterRes?.data;
  const currentQuarter = myQData?.currentQuarter ?? cycle?.quarter;

  const activeSummary = useMemo(
    () => myQData?.quarterSummary?.find((q) => q.quarter === currentQuarter),
    [myQData, currentQuarter],
  );

  const quarterStatus = activeSummary?.status ?? "locked";
  const shouldSkipTasks =
    !currentQuarter ||
    quarterStatus === "locked" ||
    quarterStatus === "upcoming";

  const { data: tasksRes, isLoading: tasksLoading } = useGetTasksQuery(
    { quarter: currentQuarter as QuarterKey },
    { skip: shouldSkipTasks },
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const allTasks = tasksRes?.data ?? [];

  const pendingTaskCount = useMemo(
    () => allTasks.filter((t) => !isCompleted(t.submission?.status)).length,
    [allTasks],
  );

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setSidebarOpen(false);
      } else {
        setSidebarOpen(true);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (window.innerWidth < 768) {
      setSidebarOpen(false);
    }
  }, [location.pathname]);

  async function handleLogout() {
    try {
      await logoutApi(undefined).unwrap();
    } catch {
      // ignore
    } finally {
      dispatch(clearCredentials());
      navigate("/");
    }
  }

  const getUserInitials = () => {
    if (user?.firstName && user?.lastName) {
      return `${user.firstName[0]}${user.lastName[0]}`.toUpperCase();
    }
    if (user?.firstName) return user.firstName[0].toUpperCase();
    if (user?.email) return user.email[0].toUpperCase();
    return "U";
  };

  const getDisplayName = () => {
    if (user?.firstName && user?.lastName) {
      return `${user.firstName} ${user.lastName}`;
    }
    if (user?.firstName) return user.firstName;
    return user?.email || "Member";
  };

  const isActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(`${path}/`);

  const taskBadge = (() => {
    if (shouldSkipTasks) return null;
    if (tasksLoading) {
      return {
        text: "Loading…",
        color: "bg-gray-100 text-gray-600",
      };
    }
    if (pendingTaskCount > 0) {
      return {
        text: `${pendingTaskCount} due`,
        color: "bg-amber-100 text-amber-700",
      };
    }
    return null;
  })();

  const navItems = [
    {
      label: "Dashboard",
      path: "/user/dashboard",
      icon: <Home size={18} />,
      badge: null,
    },
    {
      label: "Library",
      path: "/user/library",
      icon: <BookOpen size={18} />,
      badge: null,
    },
    {
      label: "Tasks",
      path: "/user/tasks",
      icon: <ClipboardList size={18} />,
      badge: taskBadge,
    },
    {
      label: "My Journey",
      path: "/user/journey",
      icon: <TrendingUp size={18} />,
      badge: null,
    },
    {
      label: "Fellows",
      path: "/user/fellows",
      icon: <Star size={18} />,
      badge: null,
    },
    {
      label: "Community",
      path: "/user/community",
      icon: <Users size={18} />,
      badge: null,
    },
  ];

  const getPageTitle = () => {
    const match = navItems.find((item) => isActive(item.path));
    return match?.label ?? "Dashboard";
  };

  const currentTier = (user?.tier as MemberTier) ?? "wider_audience";

  const needsOnboarding = user && !user.hasCompletedOnboarding;
  const needsWelcome =
    user && user.hasCompletedOnboarding && !user.hasSeenWelcome;

  return (
    <div className="h-screen flex overflow-hidden bg-gray-50">
      {needsOnboarding && <OnboardingModal />}
      {needsWelcome && <WelcomeModal />}

      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-20 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
      <aside
        className={`
          ${sidebarOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"}
          ${sidebarOpen ? "md:w-64" : "md:w-0"}
          fixed md:static inset-y-0 left-0 z-30 w-64 bg-white flex-shrink-0 transition-all duration-300 border-r overflow-hidden
        `}
      >
        <div className="h-full flex flex-col">
          {/* Brand */}
          <div className="p-4 border-b">
            <Link to="/user/home" className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-green-400 to-green-600 flex items-center justify-center font-bold text-lg text-white">
                G
              </div>
              <div>
                <div className="font-semibold">GIMLP</div>
                <div className="text-xs text-gray-500">Member Portal</div>
              </div>
            </Link>
          </div>

          {/* QUARTER CONTEXT PILL */}
          <div className="px-4 py-3 border-b bg-green-50">
            <p className="text-xs text-green-600 font-medium uppercase tracking-wide">
              Current Quarter
            </p>

            {quarterLoading ? (
              <>
                <div className="mt-1 h-4 w-44 rounded bg-green-100 animate-pulse" />
                <div className="mt-2 h-1.5 rounded-full bg-green-100 overflow-hidden">
                  <div className="h-full w-1/3 rounded-full bg-green-200 animate-pulse" />
                </div>
                <div className="mt-2 h-3 w-28 rounded bg-green-100 animate-pulse" />
              </>
            ) : cycle ? (
              <>
                <p className="text-sm font-semibold text-green-800 mt-0.5">
                  {cycle.quarter}
                  {cycle.theme ? ` · ${cycle.theme}` : ""}
                </p>
                <div className="mt-2 h-1.5 rounded-full bg-green-100 overflow-hidden">
                  <div
                    className="h-full rounded-full bg-green-500 transition-all duration-700"
                    style={{ width: `${cycle.progressPercent ?? 0}%` }}
                  />
                </div>
                <p className="text-xs text-green-600 mt-1">
                  {cycle.daysToEvent ?? "—"} days to next event
                </p>
              </>
            ) : (
              <>
                <p className="text-sm font-semibold text-green-800 mt-0.5">—</p>
                <div className="mt-2 h-1.5 rounded-full bg-green-100 overflow-hidden">
                  <div className="h-full w-0 rounded-full bg-green-500" />
                </div>
                <p className="text-xs text-green-600 mt-1">
                  — days to next event
                </p>
              </>
            )}
          </div>

          {/* NAV TABS */}
          <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors
                  ${
                    isActive(item.path)
                      ? "bg-green-50 text-green-700"
                      : "text-gray-600 hover:bg-gray-100"
                  }
                `}
              >
                <span
                  className={`flex-shrink-0 ${
                    isActive(item.path) ? "text-green-600" : "text-gray-400"
                  }`}
                >
                  {item.icon}
                </span>

                <span className="flex-1">{item.label}</span>

                {item.badge && (
                  <span
                    className={`text-xs font-medium px-2 py-0.5 rounded-full ${item.badge.color}`}
                  >
                    {item.badge.text}
                  </span>
                )}
              </Link>
            ))}
          </nav>

          {/* SIDEBAR FOOTER */}
          <div className="p-3 border-t">
            <div className="flex items-center gap-3 mb-3">
              {user?.avatar ? (
                <img
                  src={user.avatar}
                  alt={getDisplayName()}
                  className="w-9 h-9 rounded-full object-cover"
                />
              ) : (
                <div className="w-9 h-9 rounded-full bg-green-500 flex items-center justify-center text-white text-sm font-semibold">
                  {getUserInitials()}
                </div>
              )}

              <div className="flex-1 min-w-0">
                <div className="font-medium truncate text-sm">
                  {getDisplayName()}
                </div>
                <div className="text-xs text-gray-500 truncate">
                  {user?.email}
                </div>
                {user?.role && (
                  <div className="text-xs text-gray-400 capitalize">
                    {user.role}
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-1">
              <button
                onClick={() => navigate("/user/settings")}
                className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors text-sm text-gray-600"
              >
                <Settings size={16} />
                <span>Settings</span>
              </button>

              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors text-sm text-gray-600"
              >
                <LogOut size={16} />
                <span>Logout</span>
              </button>
            </div>
          </div>
        </div>
      </aside>
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* TOP BAR */}
        <header className="bg-white border-b px-4 py-2 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-2 rounded-md hover:bg-gray-100"
            >
              {sidebarOpen ? <X size={18} /> : <Menu size={18} />}
            </button>

            <div>
              <h1 className="text-lg font-semibold text-gray-900">
                {getPageTitle()}
              </h1>
              <p className="text-xs text-gray-500">
                Welcome back, {user?.firstName || "Member"}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <span
              className={`hidden sm:inline-flex items-center gap-1.5 px-3 py-1 rounded-full border text-xs font-medium ${
                TIER_STYLES[currentTier]
              }`}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-current inline-block opacity-70" />
              {TIER_LABELS[currentTier]}
            </span>
          </div>
        </header>

        {/* CONTENT */}
        <main className="flex-1 overflow-auto bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 py-6 md:py-8">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

export default MemberLayout;
